import type { CSSProperties } from 'react';
import { LIGHT_THEME_MODES, type ThemeMode } from '@constants/colors';

export type TariffKind = 'PPN' | 'PPH' | 'BM';

/** Convert #RRGGBB (or #RGB) to rgba(). Falls back to the original string. */
export const withHexAlpha = (color: string, alpha: number): string => {
  const raw = color.replace('#', '').trim();
  const hex =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw.slice(0, 6);
  if (hex.length !== 6 || Number.isNaN(Number.parseInt(hex, 16))) {
    return color;
  }
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Horizon-cohesive tariff hues from theme tokens (same family, subtle differentiation):
 * PPN → accent, PPH → primary, BM → muted textSecondary.
 */
export function getTariffBadgeBaseColor(kind: TariffKind, themeColors: Record<string, string>): string {
  switch (kind) {
    case 'PPN':
      return themeColors.accent || themeColors.primary;
    case 'PPH':
      return themeColors.primary || themeColors.buttonBackground;
    case 'BM':
      return themeColors.textSecondary || themeColors.accent;
    default:
      return themeColors.accent || themeColors.primary;
  }
}

/** 0–1 intensity from tariff % — encodes rate without traffic-light hues. */
export function getTariffFillIntensity(tariffValue: number): number {
  if (tariffValue === 0) return 0;
  if (tariffValue <= 2.5) return 0.25;
  if (tariffValue <= 5) return 0.4;
  if (tariffValue <= 7.5) return 0.55;
  if (tariffValue <= 10) return 0.7;
  if (tariffValue <= 15) return 0.85;
  return 1;
}

/**
 * Soft filled badges on light themes (dark teal/slate text on tint).
 * Deeper fills + light text on dark themes.
 */
export function getTariffBadgeStyle(
  kind: TariffKind,
  tariffValue: number,
  themeColors: Record<string, string>,
  mode: ThemeMode,
  extras: CSSProperties = {}
): CSSProperties {
  const base = getTariffBadgeBaseColor(kind, themeColors);
  const intensity = getTariffFillIntensity(tariffValue);
  const isLight = (LIGHT_THEME_MODES as readonly string[]).includes(mode);

  if (isLight) {
    const bgAlpha = 0.12 + intensity * 0.18;
    return {
      backgroundColor: withHexAlpha(base, bgAlpha),
      color: base,
      border: `1px solid ${withHexAlpha(base, 0.28 + intensity * 0.22)}`,
      textShadow: 'none',
      ...extras,
    };
  }

  const bgAlpha = 0.4 + intensity * 0.5;
  return {
    backgroundColor: withHexAlpha(base, bgAlpha),
    color: themeColors.buttonText || '#FFFFFF',
    border: `1px solid ${withHexAlpha(base, 0.5)}`,
    textShadow: bgAlpha < 0.65 ? '0 1px 2px rgba(0,0,0,0.45)' : 'none',
    ...extras,
  };
}

/** Count chips (Total / Dipilih / etc.) — filled primary CTA, not Mantine blue. */
export function getCountBadgeStyle(themeColors: Record<string, string>): CSSProperties {
  return {
    backgroundColor: themeColors.buttonBackground || themeColors.primary,
    color: themeColors.buttonText || '#FFFFFF',
    border: 'none',
  };
}

/**
 * Soft teal/sand/slate family for HS group indices — same Horizon family, subtle
 * rotation. Avoids neon rainbow (blue/red/green/amber/violet/pink/…).
 */
export function getGroupBadgePalette(themeColors: Record<string, string>): string[] {
  const palette = [
    themeColors.accent,
    themeColors.primary || themeColors.buttonBackground,
    themeColors.info || themeColors.blue,
    themeColors.textSecondary,
    themeColors.buttonHover || themeColors.primaryHover,
    themeColors.placeholder || themeColors.textSecondary,
  ].filter(Boolean) as string[];
  return palette.length > 0 ? palette : [themeColors.text || '#1A2E2B'];
}

/** Resolve a group label ("1", "2", …) or 0-based index to a soft family hue. */
export function getGroupBadgeBaseColor(
  labelOrIndex: string | number,
  themeColors: Record<string, string>
): string {
  const palette = getGroupBadgePalette(themeColors);
  const index =
    typeof labelOrIndex === 'number' ? labelOrIndex : Number(labelOrIndex) - 1;
  if (!Number.isFinite(index) || index < 0) {
    return themeColors.textSecondary || palette[0];
  }
  return palette[index % palette.length];
}

/**
 * Soft group number chip: tinted fill + matching text (light themes) or deeper
 * fill + light text (dark themes). No solid neon blocks.
 */
export function getGroupBadgeStyle(
  labelOrIndex: string | number,
  themeColors: Record<string, string>,
  mode: ThemeMode,
  extras: CSSProperties = {}
): CSSProperties {
  const base = getGroupBadgeBaseColor(labelOrIndex, themeColors);
  const isLight = (LIGHT_THEME_MODES as readonly string[]).includes(mode);

  if (isLight) {
    return {
      backgroundColor: withHexAlpha(base, 0.14),
      color: base,
      border: `1px solid ${withHexAlpha(base, 0.32)}`,
      fontWeight: 700,
      ...extras,
    };
  }

  return {
    backgroundColor: withHexAlpha(base, 0.45),
    color: themeColors.buttonText || '#FFFFFF',
    border: `1px solid ${withHexAlpha(base, 0.5)}`,
    fontWeight: 700,
    ...extras,
  };
}

/** Column header color for PPN / PPH / BM — same soft family as cell badges. */
export function getTariffColumnHeaderColor(
  kind: TariffKind,
  themeColors: Record<string, string>
): string {
  return getTariffBadgeBaseColor(kind, themeColors);
}
